import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Crosshair,
  ChevronRight,
  ChevronLeft,
  Rocket,
  Copy,
  Check,
  AlertTriangle,
  Tag,
  Loader,
  Terminal,
  ShieldCheck,
  Layers,
} from 'lucide-react';
import { beaconsAPI, monitorAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const TOTAL_STEPS = 3;

// Fallback used only if /install-config is unreachable. Production must override
// via backend's SHINOBI_PUBLIC_API_URL env var, surfaced through install-config.
const INSTALL_CONFIG_FALLBACK = {
  backend_url: process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : ''),
  beacon_binary_base_url: 'https://shinobi.milot.io/beacons',
  profile_binaries: {
    pentester: 'shinobi-pentest-beacon',
    dast: 'shinobi-pentest-beacon',
    internal_scanner: 'shinobi-monitor-beacon',
    network_monitor: 'shinobi-monitor-beacon',
  },
};

// Hardcoded fallback if the /profiles endpoint is unreachable. Mirrors the
// backend's PROFILE_METADATA in app/api/v2/beacon_deploy.py.
const PROFILE_FALLBACK = [
  {
    name: 'pentester',
    label: 'Pen Tester',
    os_hint: 'Kali Linux',
    description: 'Full offensive toolkit. Entire scanner roster plus AI orchestrator kali_command allowlist.',
    tool_bundle: ['nuclei', 'zaproxy', 'sqlmap', 'ffuf', 'gobuster', 'nikto', 'wpscan'],
  },
  {
    name: 'dast',
    label: 'DAST',
    os_hint: 'Ubuntu 22.04+',
    description: 'Web app testing. Nuclei web templates and OWASP ZAP, plus passive header/TLS/tech checks.',
    tool_bundle: ['nuclei', 'zaproxy'],
  },
  {
    name: 'internal_scanner',
    label: 'Internal Scanner',
    os_hint: 'Ubuntu 22.04+',
    description: 'Internal network scanning. Nuclei network templates and nmap. OpenVAS deferred.',
    tool_bundle: ['nuclei', 'nmap'],
  },
];

const TagInput = ({ tags, setTags, maxTags = 10 }) => {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  const suggestions = ['kali', 'prod', 'staging', 'dmz', 'internal', 'lab', 'redteam'];

  const addTag = (tag) => {
    const cleaned = tag.trim().toLowerCase().replace(/[^a-z0-9-_:]/g, '');
    if (cleaned && !tags.includes(cleaned) && tags.length < maxTags) {
      setTags([...tags, cleaned]);
    }
    setInput('');
  };

  const removeTag = (tag) => setTags(tags.filter((t) => t !== tag));

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      addTag(input);
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const availableSuggestions = suggestions.filter((s) => !tags.includes(s));

  return (
    <div>
      <div
        className="terminal-input w-full rounded-lg px-3 py-2 flex flex-wrap gap-2 cursor-text min-h-[44px]"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="flex items-center space-x-1 rounded-full px-3 py-0.5 text-xs font-mono"
            style={{
              background: 'rgba(72, 185, 76, 0.12)',
              color: '#389a3c',
              border: '1px solid rgba(72, 185, 76, 0.35)',
            }}
          >
            <span>#{tag}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="ml-1 hover:text-slate-900 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length < maxTags ? 'Type and press Enter…' : `Max ${maxTags} tags`}
          disabled={tags.length >= maxTags}
          className="bg-transparent outline-none text-sm text-slate-800 placeholder-gray-600 flex-1 min-w-[120px]"
        />
      </div>

      {availableSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-xs text-gray-600 self-center">Suggest:</span>
          {availableSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              disabled={tags.length >= maxTags}
              className="text-xs px-2.5 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:border-wasabi-300 hover:text-shinobi-green transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              #{s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StepDot = ({ index, current }) => {
  const isDone = index < current;
  const isCurrent = index === current;
  return (
    <div className="flex items-center">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all`}
        style={
          isDone
            ? {
                background: '#48b94c',
                color: '#ffffff',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
              }
            : isCurrent
            ? {
                background: 'rgba(72, 185, 76, 0.12)',
                color: '#389a3c',
                border: '1px solid #48b94c',
              }
            : {
                background: '#f1f5f9',
                color: '#94a3b8',
                border: '1px solid #e2e8f0',
              }
        }
      >
        {isDone ? <Check className="w-3.5 h-3.5" /> : index}
      </div>
    </div>
  );
};

// Profiles that run the monitor beacon binary. Blue Teamers may only deploy
// these (the backend also enforces this on deploy-key creation).
const MONITOR_PROFILE_NAMES = ['internal_scanner', 'network_monitor'];

// initialVault / initialProfile let callers (e.g. Asset Inventory) open the
// modal pre-targeted at a monitoring vault and the Internal Scanner profile.
const DeployBeaconModal = ({ isOpen, onClose, initialVault = '', initialProfile = null }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'pentester';
  const isBlueTeam = user?.role === 'blue_team';
  // Blue Teamers are scoped to monitor profiles; everyone else defaults to pentester.
  const defaultProfile = initialProfile || (isBlueTeam ? 'internal_scanner' : 'pentester');
  const filterProfilesForRole = (list) =>
    isBlueTeam ? list.filter((p) => MONITOR_PROFILE_NAMES.includes(p.name)) : list;

  const [step, setStep] = useState(1);

  const [profile, setProfile] = useState(defaultProfile);
  const [profiles, setProfiles] = useState(() => filterProfilesForRole(PROFILE_FALLBACK));
  const [label, setLabel] = useState('');
  const [tags, setTags] = useState([]);
  const [vault, setVault] = useState(initialVault || '');
  const [vaultOptions, setVaultOptions] = useState([]);
  const [networkLocation, setNetworkLocation] = useState('external');
  const [labelError, setLabelError] = useState('');

  const [deployKey, setDeployKey] = useState(null);
  const [deployLoading, setDeployLoading] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [installConfig, setInstallConfig] = useState(INSTALL_CONFIG_FALLBACK);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setProfile(defaultProfile);
      setLabel('');
      setVault(initialVault || '');
      setTags([]);
      setNetworkLocation('external');
      setLabelError('');
      setDeployKey(null);
      setDeployLoading(false);
      setDeployError(null);
      setCopied(false);
      beaconsAPI
        .listBeaconProfiles()
        .then((res) => {
          let list = res?.data?.profiles?.filter((p) => p.name !== 'network_monitor');
          if (Array.isArray(list)) list = filterProfilesForRole(list);
          if (Array.isArray(list) && list.length > 0) setProfiles(list);
        })
        .catch(() => {});
      // Existing vault labels so the operator can drop a new beacon into an
      // established vault instead of retyping (and risking a typo that forks it).
      monitorAPI
        .listVaults()
        .then((res) => {
          const names = (res?.data?.vaults || [])
            .map((v) => v.vault)
            .filter((name) => typeof name === 'string' && name.trim());
          setVaultOptions(names);
        })
        .catch(() => setVaultOptions([]));
      beaconsAPI
        .getInstallConfig()
        .then((res) => {
          if (res?.data?.backend_url) setInstallConfig(res.data);
        })
        .catch(() => {});
    }
  }, [isOpen, initialVault, initialProfile]);

  useEffect(() => {
    if (step !== 3 || deployKey || deployLoading) return;
    const fetchKey = async () => {
      setDeployLoading(true);
      setDeployError(null);
      try {
        const response = await beaconsAPI.createDeployKey({ label: label.trim(), profile });
        setDeployKey(response.data);
      } catch (err) {
        console.error('Failed to create deploy key:', err);
        setDeployError(err.response?.data?.detail || 'Failed to generate deploy key. Please try again.');
      } finally {
        setDeployLoading(false);
      }
    };
    fetchKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const buildInstallCommand = (key) => {
    const binaryName = installConfig.profile_binaries?.[profile] || 'shinobi-pentest-beacon';
    const binaryUrl = `${installConfig.beacon_binary_base_url}/${binaryName}`;
    const isMonitor = binaryName === 'shinobi-monitor-beacon';
    const flags = [
      `--backend ${installConfig.backend_url}`,
      `--api-key ${key}`,
      `--label "${label.trim()}"`,
      `--profile ${profile}`,
      `--location ${networkLocation}`,
    ];
    if (tags.length > 0) flags.push(`--tags ${tags.join(',')}`);
    if (isMonitor && vault.trim()) flags.push(`--vault "${vault.trim()}"`);
    const header =
      `BINARY_URL=${binaryUrl} \\\n` +
      `curl -sSL ${installConfig.beacon_binary_base_url}/install.sh | sudo -E bash -s -- \\`;
    return `${header}\n  ${flags.join(' \\\n  ')}`;
  };

  const handleCopy = () => {
    if (!deployKey) return;
    navigator.clipboard.writeText(buildInstallCommand(deployKey.key)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleStep1Next = () => {
    // Profile is always set (default pentester); no validation needed.
    setStep(2);
  };

  const handleStep2Next = () => {
    if (!label.trim()) {
      setLabelError('Label is required.');
      return;
    }
    setLabelError('');
    setStep(3);
  };

  const activeProfile = profiles.find((p) => p.name === profile) || profiles[0];
  const subtitleOs = activeProfile?.os_hint || 'Linux';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />

      <div
        className="relative rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white"
        style={{
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.15)',
        }}
      >
        {/* Hero strip */}
        <div
          className="relative px-7 pt-7 pb-6 overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, rgba(72, 185, 76, 0.10) 0%, rgba(72, 185, 76, 0.04) 50%, transparent 100%)',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(72, 185, 76, 0.12), transparent 70%)' }}
          />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: '#48b94c',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                }}
              >
                <Crosshair className="w-7 h-7 text-white" strokeWidth={2.4} />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight text-slate-900">Deploy a Beacon</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Headless agent on {subtitleOs} • Step {step} of {TOTAL_STEPS}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 transition-colors p-1 -m-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step rail */}
          <div className="relative mt-6 flex items-center gap-3">
            <StepDot index={1} current={step} />
            <div
              className="flex-1 h-px"
              style={{
                background:
                  step > 1
                    ? '#48b94c'
                    : '#e2e8f0',
              }}
            />
            <StepDot index={2} current={step} />
            <div
              className="flex-1 h-px"
              style={{
                background:
                  step > 2
                    ? '#48b94c'
                    : '#e2e8f0',
              }}
            />
            <StepDot index={3} current={step} />
            <span className="text-[11px] uppercase tracking-widest font-semibold text-gray-500 ml-2">
              {step === 1 ? 'Profile' : step === 2 ? 'Configure' : 'Deploy'}
            </span>
          </div>
        </div>

        {/* Step 1 — Profile */}
        {step === 1 && (
          <div className="px-7 py-6 space-y-6">
            <div>
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-shinobi-green" />
                Pick a beacon profile
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Profile is immutable after install. To switch, deregister this beacon and re-run the installer.
              </p>
            </div>

            <div className="space-y-3">
              {profiles.map((p) => {
                const active = profile === p.name;
                return (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setProfile(p.name)}
                    className="w-full text-left rounded-xl p-4 transition-all"
                    style={
                      active
                        ? {
                            background: 'rgba(72, 185, 76, 0.08)',
                            border: '1px solid #48b94c',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                          }
                        : {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                          }
                    }
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${active ? 'text-shinobi-green' : 'text-slate-900'}`}>
                            {p.label}
                          </span>
                          <span
                            className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                            style={{
                              background: '#f1f5f9',
                              color: '#475569',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            {p.os_hint}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{p.description}</p>
                        {Array.isArray(p.tool_bundle) && p.tool_bundle.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {p.tool_bundle.slice(0, 8).map((tool) => (
                              <span
                                key={tool}
                                className="text-[10px] font-mono px-1.5 py-0.5 rounded text-slate-500 bg-slate-100 border border-slate-200"
                              >
                                {tool}
                              </span>
                            ))}
                            {p.tool_bundle.length > 8 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-gray-500">
                                +{p.tool_bundle.length - 8} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {active && (
                        <div
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{
                            background: '#48b94c',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                          }}
                        >
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={handleStep1Next}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold tracking-wide transition-all"
                style={{
                  background: '#48b94c',
                  color: '#ffffff',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                }}
              >
                <span>Continue</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Configure */}
        {step === 2 && (
          <div className="px-7 py-6 space-y-6">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Configure Beacon</h3>
              <p className="text-sm text-slate-500 mt-1">
                Label this {activeProfile?.label || 'beacon'} deployment, set its network location, and add tags so you can find it later.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                Label <span className="text-shinobi-green">*</span>
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => { setLabel(e.target.value); if (labelError) setLabelError(''); }}
                placeholder="e.g. Kali Lab 01"
                className={`terminal-input w-full px-4 py-2.5 rounded-lg text-sm ${labelError ? 'border-danger-red' : ''}`}
                autoFocus
              />
              {labelError && <p className="text-danger-red text-xs mt-1.5">{labelError}</p>}
            </div>

            {(installConfig.profile_binaries?.[profile] === 'shinobi-monitor-beacon') && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <span>Vault</span>
                    <span className="text-gray-600 font-normal normal-case">optional</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={vault}
                  onChange={(e) => setVault(e.target.value)}
                  placeholder='e.g. Hillsboro (US)'
                  list="deploy-vault-options"
                  className="terminal-input w-full px-4 py-2.5 rounded-lg text-sm"
                />
                <datalist id="deploy-vault-options">
                  {vaultOptions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                {vaultOptions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="text-xs text-gray-600 self-center">Existing:</span>
                    {vaultOptions.map((name) => {
                      const active = vault.trim() === name;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setVault(active ? '' : name)}
                          className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                            active
                              ? 'border-shinobi-green/60 text-shinobi-green bg-wasabi-100'
                              : 'border-slate-200 text-slate-500 hover:border-wasabi-300 hover:text-shinobi-green'
                          }`}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-gray-600 text-xs mt-1.5">
                  Groups this monitor beacon and its discovered assets under a named vault in Asset Inventory.
                  Pick an existing vault to merge into it, or type a new name to create one. Vault data persists even if the beacon is later removed.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                <span className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-shinobi-green" />
                  <span>Tags</span>
                  <span className="text-gray-600 font-normal normal-case">optional</span>
                </span>
              </label>
              <TagInput tags={tags} setTags={setTags} maxTags={10} />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                Network Location
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'external', title: 'External', desc: 'Internet-facing perimeter' },
                  { id: 'internal', title: 'Internal', desc: 'Inside the corporate network' },
                ].map(({ id, title, desc }) => {
                  const active = networkLocation === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setNetworkLocation(id)}
                      className="text-left p-3 rounded-lg transition-all"
                      style={
                        active
                          ? {
                              background: 'rgba(72, 185, 76, 0.08)',
                              border: '1px solid #48b94c',
                              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                            }
                          : {
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                            }
                      }
                    >
                      <p className={`text-sm font-semibold ${active ? 'text-shinobi-green' : 'text-slate-800'}`}>
                        {title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <button
                onClick={handleStep2Next}
                disabled={!label.trim()}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: label.trim() ? '#48b94c' : '#e2e8f0',
                  color: label.trim() ? '#ffffff' : '#94a3b8',
                  boxShadow: label.trim() ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                }}
              >
                <span>Generate Install Command</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Deploy */}
        {step === 3 && (
          <div className="px-7 py-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{
                    background: '#48b94c',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <Rocket className="w-7 h-7 text-white" strokeWidth={2.4} />
                </div>
                <div
                  className="absolute -inset-1 rounded-xl animate-ping pointer-events-none"
                  style={{ background: 'rgba(72, 185, 76, 0.2)', animationDuration: '2.4s' }}
                />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Ready to deploy</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Run this on the target {subtitleOs} host. The installer drops the binary, registers a systemd
                  service, and starts the beacon.
                </p>
              </div>
            </div>

            {deployLoading && (
              <div className="flex items-center justify-center gap-3 py-8 text-slate-500">
                <Loader className="w-5 h-5 animate-spin text-shinobi-green" />
                <span className="text-sm">Generating deploy key…</span>
              </div>
            )}

            {deployError && (
              <div className="rounded-lg border border-danger-red/40 bg-danger-red/5 p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-danger-red flex-shrink-0 mt-0.5" />
                <p className="text-danger-red text-sm">{deployError}</p>
              </div>
            )}

            {deployKey && !deployLoading && (
              <>
                <div
                  className="relative rounded-xl overflow-hidden"
                  style={{
                    border: '1px solid #1f2937',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <div
                    className="flex items-center justify-between px-4 py-2"
                    style={{
                      background: '#0b1220',
                      borderBottom: '1px solid #1f2937',
                    }}
                  >
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Terminal className="w-3.5 h-3.5 text-shinobi-green" />
                      <span className="font-mono">install.sh</span>
                    </div>
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors"
                      style={{
                        color: copied ? '#ffffff' : '#86efac',
                        background: copied ? '#48b94c' : 'transparent',
                        border: copied ? 'none' : '1px solid rgba(134, 239, 172, 0.4)',
                      }}
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <pre
                    className="px-4 py-4 text-xs leading-relaxed overflow-x-auto"
                    style={{
                      background: '#0b1220',
                      color: '#86efac',
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {buildInstallCommand(deployKey.key)}
                  </pre>
                </div>

                <div
                  className="rounded-lg p-4 flex items-start gap-3"
                  style={{
                    background: 'rgba(251, 191, 36, 0.08)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                  }}
                >
                  <ShieldCheck className="w-4 h-4 text-cyber-yellow flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-cyber-yellow/90 leading-relaxed">
                    <span className="font-semibold text-cyber-yellow">One-time API key.</span> Copy this command now — the key isn't shown again. If you lose it, revoke the deploy key and create a new one.
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-lg text-sm font-bold tracking-wide transition-all"
                style={{
                  background: '#48b94c',
                  color: '#ffffff',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeployBeaconModal;
